import { useEffect, useRef, useState } from "react";
import { Keyboard, Platform, StyleSheet, TextInput, View } from "react-native";

import { SearchIcon } from "@/design/icons";
import { Text } from "@/design/Text";
import { fonts, palette, radius, space } from "@/design/tokens";

const SEARCH_INPUT_HEIGHT = 46;

/** Search pill — the whole control lights up on focus, not just the input. */
export function SearchInput({
  value,
  onChangeText,
  placeholder,
  onBlur,
  onFocus
}: {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  onBlur?: () => void;
  onFocus?: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (Platform.OS !== "android" || !focused) return;

    // Android's Back button dismisses the IME but normally leaves the input
    // focused. End the search interaction when the keyboard is actually gone
    // so the cursor, focus treatment, and Watch queue-follow lock all release
    // together.
    const subscription = Keyboard.addListener("keyboardDidHide", () => {
      inputRef.current?.blur();
    });
    return () => subscription.remove();
  }, [focused]);

  return (
    <View style={[styles.wrap, focused && styles.wrapFocused]}>
      <SearchIcon size={17} color={focused ? palette.accent : palette.faint} />
      <TextInput
        ref={inputRef}
        accessibilityLabel={placeholder}
        autoCapitalize="none"
        autoCorrect={false}
        cursorColor={palette.accent}
        inputMode="search"
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        onChangeText={onChangeText}
        onFocus={() => {
          setFocused(true);
          onFocus?.();
        }}
        onSubmitEditing={Keyboard.dismiss}
        placeholder={placeholder}
        placeholderTextColor={palette.faint}
        returnKeyType="search"
        selectionColor={palette.accent}
        selectionHandleColor={palette.accent}
        style={styles.input}
        value={value}
      />
      {value ? (
        <Text
          accessibilityRole="button"
          accessibilityLabel="Clear search"
          onPress={() => onChangeText("")}
          color={palette.muted}
          style={styles.clear}
        >
          ✕
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    // A cool, faintly-lifted blue kept semi-transparent: over a plain dark page
    // it composites to the subtle blue tint the pill has always had, but the
    // translucency lets a backdrop gradient bleed through so it settles into the
    // color instead of punching an opaque rectangle across it.
    backgroundColor: "rgba(19, 22, 32, 0.51)",
    borderColor: palette.hairline,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: space.md,
    height: SEARCH_INPUT_HEIGHT,
    paddingHorizontal: space.lg
  },
  wrapFocused: {
    backgroundColor: "rgba(22, 26, 40, 0.82)",
    borderColor: "rgba(143, 168, 250, 0.55)",
    ...Platform.select({
      web: { boxShadow: "0 0 0 3px rgba(143, 168, 250, 0.15)" } as object,
      default: {}
    })
  },
  input: {
    color: palette.text,
    flex: 1,
    fontFamily: fonts.body,
    fontSize: 15,
    height: "100%",
    includeFontPadding: false,
    paddingVertical: 0,
    ...Platform.select({
      android: { textAlignVertical: "center" },
      web: { outlineStyle: "none" } as object,
      default: {}
    })
  } as never,
  clear: {
    fontSize: 13,
    padding: space.xs
  }
});
